import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { CalendarRange, Grid3x3, RefreshCw, Download, ChevronRight } from "lucide-react";
import LoadingOverlay from "@/components/common/LoadingOverlay";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());

function fmtDateShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit", month: "short",
    });
  } catch {
    return iso;
  }
}

function fmtDateTimeShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      hour12: false,
    });
  } catch {
    return iso;
  }
}

// Color a cell green (healthy), amber (low), red (missing) based on the
// campaign's own average — so a low-volume niche campaign is still
// judged relative to itself.
function cellClass(rowCount, avg) {
  if (rowCount === 0) return "bg-rose-50 text-rose-700 border-rose-100";
  if (avg > 0 && rowCount < avg * 0.3) return "bg-amber-50 text-amber-800 border-amber-100";
  if (avg > 0 && rowCount < avg * 0.6) return "bg-yellow-50 text-yellow-800 border-yellow-100";
  return "bg-emerald-50 text-emerald-800 border-emerald-100";
}

/**
 * Upload coverage — bird's-eye matrix of what data landed for which
 * (brand × campaign × attempt_date), plus a timeline of when each
 * upload actually arrived.
 *
 * Two view modes:
 *   - "campaigns-rows": campaigns down, attempt_dates across (default)
 *   - "dates-rows":     attempt_dates down, campaigns across
 */
export default function UploadCoverageCard({ brandIds = [] }) {
  const today = new Date().toISOString().slice(0, 10);
  const thirtyAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000)
    .toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(thirtyAgo);
  const [dateTo, setDateTo] = useState(today);
  const [view, setView] = useState("campaigns-rows");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = { date_from: dateFrom, date_to: dateTo };
    if (brandIds.length === 1) params.brand_id = brandIds[0];
    api.get("/ops/reports/upload-coverage", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [dateFrom, dateTo, brandIds.join(",")]);

  const cellIndex = useMemo(() => {
    // Key: `${brand_id}|${campaign_id}|${date}` → cell
    const idx = new Map();
    for (const c of data?.cells || []) {
      idx.set(`${c.brand_id}|${c.campaign_id}|${c.attempt_date_ist}`, c);
    }
    return idx;
  }, [data]);

  const avgIndex = useMemo(() => {
    const idx = new Map();
    for (const a of data?.campaign_averages || []) {
      idx.set(`${a.brand_id}|${a.campaign_id}`, a.avg_rows_per_day);
    }
    return idx;
  }, [data]);

  const exportCsv = () => {
    if (!data) return;
    const lines = ["brand_id,campaign_id,campaign_name,attempt_date,row_count,first_uploaded_at"];
    for (const c of data.cells) {
      lines.push([c.brand_id, c.campaign_id,
                  `"${(c.campaign_name || "").replace(/"/g, '""')}"`,
                  c.attempt_date_ist, c.row_count,
                  c.first_uploaded_at || ""].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `upload_coverage_${dateFrom}_${dateTo}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="glass-card p-4" data-testid="upload-coverage-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="font-outfit font-semibold text-slate-900 flex items-center gap-2">
            <Grid3x3 className="w-4 h-4 text-[#0A84FF]" />
            Upload coverage
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Rows uploaded per campaign per attempt date. Red = missing, amber = below-average, green = healthy.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-[11px]"
               data-testid="coverage-view-toggle">
            <button type="button" onClick={() => setView("campaigns-rows")}
              className={`px-2 py-1 rounded ${view === "campaigns-rows" ? "bg-white shadow-sm border border-slate-200 text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="coverage-view-campaigns">
              Campaigns × Dates
            </button>
            <button type="button" onClick={() => setView("dates-rows")}
              className={`px-2 py-1 rounded ${view === "dates-rows" ? "bg-white shadow-sm border border-slate-200 text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="coverage-view-dates">
              Dates × Campaigns
            </button>
          </div>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                 className="text-xs px-2 py-1 border border-slate-200 rounded"
                 data-testid="coverage-date-from" />
          <span className="text-slate-400 text-xs">→</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                 className="text-xs px-2 py-1 border border-slate-200 rounded"
                 data-testid="coverage-date-to" />
          <button type="button" onClick={load}
            className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
            data-testid="coverage-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <button type="button" onClick={exportCsv} disabled={!data}
            className="inline-flex items-center gap-1 text-xs text-[#0A84FF] hover:underline disabled:text-slate-400"
            data-testid="coverage-csv">
            <Download className="w-3 h-3" /> CSV
          </button>
        </div>
      </div>

      <LoadingOverlay loading={loading}>
        {data && (
          <>
            <div className="mb-2 text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
              <span>{data.totals.unique_campaigns} campaigns × {data.totals.unique_dates} days</span>
              <span>· {fmt(data.totals.total_rows)} total rows</span>
              <span>· {fmt(data.totals.cells_populated)} / {fmt(data.totals.cells_expected)} cells populated</span>
            </div>

            <div className="overflow-auto max-h-[60vh] border border-slate-100 rounded"
                 data-testid="coverage-matrix">
              <CoverageMatrix view={view} data={data} cellIndex={cellIndex} avgIndex={avgIndex} />
            </div>

            <UploadTimeline uploads={data.uploads || []} />
          </>
        )}
        {!data && !loading && (
          <div className="text-sm text-slate-500 p-4 text-center">
            No coverage data in this window.
          </div>
        )}
      </LoadingOverlay>
    </div>
  );
}

function CoverageMatrix({ view, data, cellIndex, avgIndex }) {
  const { dates, campaigns } = data;
  if (dates.length === 0 || campaigns.length === 0) {
    return <div className="text-sm text-slate-500 p-4 text-center">No data.</div>;
  }
  const rows = view === "campaigns-rows" ? campaigns : dates;
  const cols = view === "campaigns-rows" ? dates : campaigns;
  const isCampRow = view === "campaigns-rows";

  // Per-column totals (sum across every campaign for that date, or every
  // date for that campaign, depending on the current view) — shown as a
  // footer row so ops can spot low-volume days at a glance.
  const colTotals = cols.map((c) => {
    let total = 0;
    for (const r of rows) {
      const brand_id = isCampRow ? r.brand_id : c.brand_id;
      const campaign_id = isCampRow ? r.campaign_id : c.campaign_id;
      const attemptDate = isCampRow ? c : r;
      const cell = cellIndex.get(`${brand_id}|${campaign_id}|${attemptDate}`);
      total += cell?.row_count || 0;
    }
    return total;
  });
  const grandTotal = colTotals.reduce((a, b) => a + b, 0);

  // Per-row totals: total rows for that campaign (or that date) across
  // the whole window. Useful as a right-hand summary column.
  const rowTotals = rows.map((r) => {
    let total = 0;
    for (const c of cols) {
      const brand_id = isCampRow ? r.brand_id : c.brand_id;
      const campaign_id = isCampRow ? r.campaign_id : c.campaign_id;
      const attemptDate = isCampRow ? c : r;
      const cell = cellIndex.get(`${brand_id}|${campaign_id}|${attemptDate}`);
      total += cell?.row_count || 0;
    }
    return total;
  });

  return (
    <table className="text-[11px] w-full border-collapse">
      <thead className="sticky top-0 z-10">
        <tr className="bg-slate-100">
          <th className="px-2 py-1.5 text-left font-medium text-slate-700 sticky left-0 bg-slate-100 z-20">
            {isCampRow ? "Brand · Campaign" : "Date"}
          </th>
          {cols.map((c) => (
            <th key={isCampRow ? c : c.campaign_id}
                className="px-1.5 py-1.5 text-center font-normal text-slate-600 whitespace-nowrap">
              {isCampRow ? fmtDateShort(c) : (
                <span title={`Brand ${c.brand_id}`}>
                  <span className="text-slate-400 text-[9px]">B{c.brand_id}</span>
                  {" · "}{c.campaign_name}
                </span>
              )}
            </th>
          ))}
          <th className="px-2 py-1.5 text-right font-semibold text-slate-700 whitespace-nowrap bg-slate-100">
            Total
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => {
          const rowKey = isCampRow ? `${r.brand_id}|${r.campaign_id}` : r;
          const label = isCampRow
            ? <><span className="text-slate-400 text-[9px]">B{r.brand_id}</span> · {r.campaign_name}</>
            : fmtDateShort(r);
          return (
            <tr key={rowKey} className="hover:bg-slate-50/60">
              <td className="px-2 py-1 border-t border-slate-100 sticky left-0 bg-white z-10 font-medium text-slate-800 whitespace-nowrap">
                {label}
              </td>
              {cols.map((c) => {
                const brand_id = isCampRow ? r.brand_id : c.brand_id;
                const campaign_id = isCampRow ? r.campaign_id : c.campaign_id;
                const attemptDate = isCampRow ? c : r;
                const key = `${brand_id}|${campaign_id}|${attemptDate}`;
                const cell = cellIndex.get(key);
                const avg = avgIndex.get(`${brand_id}|${campaign_id}`) || 0;
                const n = cell?.row_count || 0;
                const uploaded = cell?.first_uploaded_at;
                const uploadedLabel = uploaded ? `uploaded ${fmtDateTimeShort(uploaded)}` : "no upload";
                const tooltip = `${n.toLocaleString()} rows · ${uploadedLabel}`;
                return (
                  <td key={key}
                      className={`px-1.5 py-1 border border-slate-100 text-center tabular-nums ${cellClass(n, avg)}`}
                      title={tooltip}
                      data-testid={`coverage-cell-${brand_id}-${campaign_id}-${attemptDate}`}>
                    {n > 0 ? n.toLocaleString() : "—"}
                  </td>
                );
              })}
              <td className="px-2 py-1 border-t border-slate-100 text-right font-semibold tabular-nums text-slate-800 bg-slate-50/60 whitespace-nowrap"
                  data-testid={`coverage-row-total-${ri}`}>
                {rowTotals[ri].toLocaleString()}
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot className="sticky bottom-0 z-10">
        <tr className="bg-slate-100 border-t-2 border-slate-300">
          <td className="px-2 py-1.5 font-semibold text-slate-800 sticky left-0 bg-slate-100 z-20 whitespace-nowrap">
            Total {isCampRow ? "per day" : "per campaign"}
          </td>
          {colTotals.map((t, i) => (
            <td key={i}
                className="px-1.5 py-1.5 text-center font-semibold tabular-nums text-slate-800 border-x border-slate-200"
                data-testid={`coverage-col-total-${i}`}>
              {t > 0 ? t.toLocaleString() : "—"}
            </td>
          ))}
          <td className="px-2 py-1.5 text-right font-bold tabular-nums text-slate-900 bg-slate-200 whitespace-nowrap"
              data-testid="coverage-grand-total">
            {grandTotal.toLocaleString()}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

function UploadTimeline({ uploads }) {
  const [expanded, setExpanded] = useState(true);
  if (!uploads || uploads.length === 0) return null;
  return (
    <div className="mt-4 border-t border-slate-100 pt-3">
      <button type="button" onClick={() => setExpanded((v) => !v)}
        className="text-xs text-slate-700 font-medium flex items-center gap-1"
        data-testid="coverage-timeline-toggle">
        <CalendarRange className="w-3.5 h-3.5" />
        Upload timeline
        <span className="text-slate-400">({uploads.length} uploads in window)</span>
        <ChevronRight className={`w-3 h-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>
      {expanded && (
        <div className="mt-2 overflow-auto max-h-64 border border-slate-100 rounded">
          <table className="text-[11px] w-full" data-testid="coverage-timeline-table">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left">
                <th className="px-2 py-1.5">Uploaded at</th>
                <th className="px-2 py-1.5">By</th>
                <th className="px-2 py-1.5">Brand</th>
                <th className="px-2 py-1.5">Filename</th>
                <th className="px-2 py-1.5 text-right">Rows</th>
                <th className="px-2 py-1.5">Call date range</th>
                <th className="px-2 py-1.5 text-right"
                    title="Days between the latest call in the file and when it was uploaded. High values = data landed late.">
                  Lag
                </th>
              </tr>
            </thead>
            <tbody>
              {uploads.map((u) => {
                const lag = u.upload_lag_days;
                const lagCls = lag == null ? "text-slate-400"
                  : lag <= 1 ? "text-emerald-700"
                  : lag <= 3 ? "text-amber-700"
                  : "text-rose-700 font-semibold";
                return (
                  <tr key={u.upload_id} className="border-t border-slate-50">
                    <td className="px-2 py-1 whitespace-nowrap">{fmtDateTimeShort(u.uploaded_at)}</td>
                    <td className="px-2 py-1 text-slate-600 truncate max-w-[180px]"
                        title={u.uploaded_by}>{u.uploaded_by || "—"}</td>
                    <td className="px-2 py-1 text-slate-600">B{u.brand_id}</td>
                    <td className="px-2 py-1 text-slate-700 font-mono truncate max-w-[220px]"
                        title={u.filename}>{u.filename || "—"}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmt(u.row_count)}</td>
                    <td className="px-2 py-1 text-slate-600 whitespace-nowrap">
                      {u.min_attempt_date === u.max_attempt_date
                        ? fmtDateShort(u.min_attempt_date)
                        : `${fmtDateShort(u.min_attempt_date)} → ${fmtDateShort(u.max_attempt_date)}`}
                    </td>
                    <td className={`px-2 py-1 text-right tabular-nums ${lagCls}`}>
                      {lag == null ? "—" : `${lag}d`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
